import React, { useEffect, useState } from "react";
import { Formik, ErrorMessage } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import * as LanguageService from "../../../services/languages.services";
import Breadcrums from "../../../common/breadcrumbs";
import Loader from "../../../common/loader";
import { SWAL_SETTINGS } from "../../../../../utils/Constants";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../../utils/commonfunction";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

const LanguageEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();

  const [submitted, setSubmitted] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [initialValues, setInitialValues] = useState({
    title: "",
    code: "",
    sort_order: 0,
  });

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    {
      title: t("Languages"),
      url: `/admin/cms/languages/list/${params.pgno || 1}`,
    },
    { title: t("link_edit"), url: "" },
  ];

  useEffect(() => {
    if (params.id) {
      LanguageService.Details(params.id)
        .then((res) => {
          if (res.success && res.data) {
            setInitialValues({
              title: res.data.title || "",
              code: res.data.code || "",
              sort_order: res.data.sort_order !== undefined ? res.data.sort_order : 0,
            });
          }
          setDataLoaded(true);
        })
        .catch((err) => {
          console.log("Error loading details:", err);
          setDataLoaded(true);
        });
    }
  }, [params.id]);

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .required(t("Title is required"))
      .max(50, t("Max allowed length of characters is 50"))
      .test("no-spaces", t("Title is required"), (value) => value && value.trim().length > 0),
    code: Yup.string()
      .required(t("Language Code is required"))
      .max(10, t("Max allowed length is 10 characters"))
      .test("no-spaces", t("Language Code is required"), (value) => value && value.trim().length > 0),
    sort_order: Yup.number()
      .typeError(t("Sort order must be a number"))
      .min(0, t("Minimum value is 0")),
  });

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-body">
            <h6 className="main-content-label mb-4">{t("EDIT LANGUAGE")}</h6>
            {!dataLoaded ? (
              <Loader />
            ) : (
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validateOnBlur={false}
                validationSchema={validationSchema}
                onSubmit={(values, { setSubmitting }) => {
                  globalLoader(true);
                  setSubmitted(true);

                  const formData = new FormData();
                  formData.append("o_id", params.id);
                  formData.append("title", values.title.trim());
                  formData.append("code", values.code.trim().toLowerCase());
                  formData.append("sort_order", values.sort_order || 0);

                  LanguageService.Edit(formData)
                    .then((response) => {
                      setSubmitting(false);
                      if (response.success) {
                        Swal.fire({
                          icon: "success",
                          text: response.message,
                          ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setTimeout(() => {
                          navigate(
                            `/admin/cms/languages/list/${params.pgno || 1}`
                          );
                        }, 1500);
                      } else {
                        Swal.fire({
                          icon: "error",
                          text: handleServerValidations(response),
                          ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setSubmitted(false);
                      }
                    })
                    .catch((error) => {
                      console.log("error ====> ", error);
                      globalLoader(false);
                      setSubmitted(false);
                    });
                }}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                }) => (
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-6 col-12">
                        <div className="form-group">
                          <label className="form-label">
                            {t("Title")} <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            name="title"
                            className={
                              "form-control " +
                              (errors.title && touched.title
                                ? "is-invalid"
                                : "")
                            }
                            placeholder={t("Enter Title")}
                            value={values.title}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <ErrorMessage
                            name="title"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </div>

                      <div className="col-md-6 col-12">
                        <div className="form-group">
                          <label className="form-label">
                            {t("Language Code")}{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            name="code"
                            className={
                              "form-control " +
                              (errors.code && touched.code ? "is-invalid" : "")
                            }
                            placeholder={t("Enter Language Code (e.g. hi, en)")}
                            value={values.code}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <ErrorMessage
                            name="code"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </div>

                      <div className="col-md-6 col-12">
                        <div className="form-group">
                          <label className="form-label">
                            {t("Sort Order")}
                          </label>
                          <input
                            type="number"
                            name="sort_order"
                            className={
                              "form-control " +
                              (errors.sort_order && touched.sort_order
                                ? "is-invalid"
                                : "")
                            }
                            placeholder={t("Enter Sort Order")}
                            value={values.sort_order}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <ErrorMessage
                            name="sort_order"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </div>

                      <div className="col-12 mt-4">
                        <button
                          type="submit"
                          disabled={submitted}
                          className="btn ripple btn-main-primary signbtn me-2"
                        >
                          {t("btn_submit")}
                        </button>
                        <button
                          type="button"
                          className="btn ripple btn-secondary"
                          onClick={() =>
                            navigate(
                              `/admin/cms/languages/list/${params.pgno || 1}`
                            )
                          }
                        >
                          {t("btn_cancel")}
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </Formik>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LanguageEdit;
